/**
 * AI Ops Engine — the shared staffing intelligence classifier.
 *
 * ONE source of truth for how every shift is classified and framed
 * across the admin, agency, and client portals. Given a raw shift row
 * from `GET /api/admin/shifts` (or an equivalent role-scoped endpoint),
 * this returns a `StaffingIntelligence` object:
 *
 *   {
 *     category:       one of CATEGORIES.id
 *     categoryLabel:  human-readable name (role-appropriate)
 *     tone:           'critical' | 'warn' | 'watch' | 'good' | 'neutral'
 *     headline:       ONE self-explaining sentence
 *                       (e.g. "Needs 1 more Brand Ambassador before Aug 15")
 *     explanation:    optional second sentence (why we're flagging it)
 *     urgencyScore:   0–100  (drives sort order + severity)
 *     hoursUntilStart:number | null
 *     actions:        [{ id, label, testid }, ...]   role-appropriate
 *   }
 *
 * PHASE 1 SCOPE (2026-08-05):
 *   • Rule-based, deterministic, zero LLM cost.
 *   • Role-aware copy: 'admin' / 'agency' get the full ops phrasing;
 *     'client' gets the softer business-focused phrasing.
 *   • Actions are surfaced but the one-tap wiring lives in Phase 2 —
 *     for now they route to the existing screens.
 *
 * Contract note: never mutate the input shift. Never throw. If a field
 * is missing we degrade gracefully.
 */

import { hoursUntilStart as hoursUntilStartTz } from './shiftDateTime';


/* ----------------------------------------------------------- categories --- */

export const CATEGORIES = [
  { id: 'critical',              label: 'Critical',              tone: 'critical', order: 1  },
  { id: 'at_risk',               label: 'At Risk',               tone: 'critical', order: 2  },
  { id: 'needs_applicants',      label: 'Needs Applicants',      tone: 'warn',     order: 3  },
  { id: 'reviewing_applicants',  label: 'Reviewing Applicants',  tone: 'warn',     order: 4  },
  { id: 'awaiting_confirmation', label: 'Awaiting Confirmation', tone: 'watch',    order: 5  },
  { id: 'partially_staffed',     label: 'Partially Staffed',     tone: 'watch',    order: 6  },
  { id: 'backup_needed',         label: 'Backup Needed',         tone: 'watch',    order: 7  },
  { id: 'briefing_incomplete',   label: 'Briefing Incomplete',   tone: 'watch',    order: 8  },
  { id: 'fully_staffed',         label: 'Fully Staffed',         tone: 'good',     order: 9  },
  { id: 'in_progress',           label: 'In Progress',           tone: 'neutral',  order: 10 },
  { id: 'completed',             label: 'Completed',             tone: 'neutral',  order: 11 },
];

export const CATEGORY_LOOKUP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

/* Tailwind palette per tone — every consumer imports this so tokens stay
 * consistent across pages. */
export const TONE_STYLES = {
  critical: {
    dot:    'bg-rose-500',
    chip:   'bg-rose-100 text-rose-800 border-rose-300',
    ring:   'ring-rose-300',
    card:   'border-rose-300 bg-rose-50/40',
    text:   'text-rose-800',
    hover:  'hover:border-rose-400',
  },
  warn: {
    dot:    'bg-amber-500',
    chip:   'bg-amber-100 text-amber-900 border-amber-300',
    ring:   'ring-amber-300',
    card:   'border-amber-300 bg-amber-50/40',
    text:   'text-amber-900',
    hover:  'hover:border-amber-400',
  },
  watch: {
    dot:    'bg-violet-500',
    chip:   'bg-violet-100 text-violet-800 border-violet-300',
    ring:   'ring-violet-300',
    card:   'border-violet-200 bg-violet-50/40',
    text:   'text-violet-800',
    hover:  'hover:border-violet-400',
  },
  good: {
    dot:    'bg-emerald-500',
    chip:   'bg-emerald-100 text-emerald-800 border-emerald-300',
    ring:   'ring-emerald-300',
    card:   'border-emerald-200 bg-emerald-50/40',
    text:   'text-emerald-800',
    hover:  'hover:border-emerald-400',
  },
  neutral: {
    dot:    'bg-slate-400',
    chip:   'bg-slate-100 text-slate-700 border-slate-300',
    ring:   'ring-slate-300',
    card:   'border-slate-200 bg-white',
    text:   'text-slate-700',
    hover:  'hover:border-slate-400',
  },
};

/* --------------------------------------------------------------- helpers --- */

const HOUR_MS = 3600 * 1000;
const DAY_MS = 24 * HOUR_MS;

function hoursUntil(shiftDate, startTime, shift) {
  // Timezone-authoritative countdown. Prefer the full shift object (carries
  // the event timezone / UTC instant / booking override); fall back to the
  // legacy date+time signature only when no shift is supplied.
  if (shift) {
    const h = hoursUntilStartTz(shift);
    if (h !== null) return h;
  }
  if (!shiftDate) return null;
  const iso = startTime
    ? `${shiftDate}T${String(startTime).slice(0, 8)}`
    : `${shiftDate}T09:00:00`;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (t - Date.now()) / HOUR_MS;
}

function pluralize(n, singular, plural) {
  return n === 1 ? singular : (plural || `${singular}s`);
}

function friendlyDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const diffDays = Math.round((d - new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00')) / DAY_MS);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays > 0 && diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Normalise the incoming shift so both /admin/shifts and future
 *  agency/client-scoped endpoints can feed the same engine. */
function normalise(shift) {
  const needed        = Number(shift.talent_needed ?? shift.workers_needed ?? shift.openings ?? 0);
  const staffed       = Number(shift.staffed_count ?? shift.confirmed_count ?? shift.workers_filled ?? 0);
  const applicants    = Number(shift.applicant_count ?? shift.applicants_count ?? shift.applicants ?? 0);
  const backupsNeeded = Number(shift.backups_needed ?? 0);
  const backupsHave   = Number(shift.backup_count ?? 0);
  const primaryCount  = Number(shift.primary_count ?? staffed);
  const dayMissed     = Number(shift.day_confirm_missed_count ?? 0);
  const status        = String(shift.status || '').toLowerCase();
  const bucket        = String(shift.bucket || '').toLowerCase();
  const hUntil        = hoursUntil(shift.shift_date, shift.start_time, shift);
  return {
    needed, staffed, applicants, backupsNeeded, backupsHave, primaryCount,
    dayMissed, status, bucket, hUntil,
    roleName: shift.role_name || shift.gig_role_name || 'staff',
    shiftDate: shift.shift_date,
    briefingPct: Number(shift.briefing_pct ?? shift.event_briefing_pct ?? 100),
    // urgency_replacement_active flag from backend
    urgentReplacement: Boolean(shift.urgent_replacement_active),
  };
}

/* --------------------------------------------------- classifier per role --- */

/**
 * Classify a single shift row.
 *
 * @param {object} shift  raw shift row (see /api/admin/shifts response)
 * @param {object} opts
 * @param {'admin'|'agency'|'client'} [opts.role='admin']  copy variant
 * @returns {object} StaffingIntelligence
 */
export function classifyShift(shift, opts = {}) {
  const role = opts.role || 'admin';
  const n = normalise(shift);

  // --- Terminal statuses first (bucket wins) ---
  if (n.bucket === 'archived' || n.bucket === 'closed' || n.status === 'completed' || n.bucket === 'completed') {
    return build('completed', {
      headline:    role === 'client' ? 'Event completed.' : 'Shift completed.',
      explanation: '',
      urgency:     0,
    }, n, role);
  }
  if (n.bucket === 'in_progress' || n.status === 'in_progress') {
    return build('in_progress', {
      headline:    role === 'client' ? 'Your event is happening now.' : 'Shift in progress.',
      explanation: n.staffed < n.needed
        ? `${n.staffed} of ${n.needed} staff clocked in.`
        : `${n.staffed} ${pluralize(n.staffed, 'person', 'people')} clocked in.`,
      urgency:     35,
    }, n, role);
  }

  // --- Cancelled ---
  if (n.status === 'cancelled') {
    return build('completed', {
      headline:    role === 'client' ? 'Event cancelled.' : 'Shift cancelled.',
      explanation: '',
      urgency:     0,
    }, n, role);
  }

  const missing = Math.max(0, n.needed - n.staffed);
  const startingSoon48 = n.hUntil !== null && n.hUntil <= 48 && n.hUntil > 0;
  const startingSoon168 = n.hUntil !== null && n.hUntil <= 24 * 7 && n.hUntil > 0;

  // --- CRITICAL: within 48h and unfilled, OR urgent replacement flagged ---
  if (missing > 0 && startingSoon48) {
    return build('critical', {
      headline: role === 'client'
        ? `${missing} ${pluralize(missing, 'position')} still open — event starts ${friendlyDate(n.shiftDate)}.`
        : `Needs ${missing} more ${n.roleName} before ${friendlyDate(n.shiftDate)} — starts in ${Math.round(n.hUntil)}h.`,
      explanation: n.applicants > 0
        ? `${n.applicants} ${pluralize(n.applicants, 'applicant')} waiting for review.`
        : (role === 'client'
            ? 'No applicants yet — we recommend requesting staffing help.'
            : 'No pending applicants — invite talent or promote a backup.'),
      urgency: 100 - Math.min(48, Math.max(0, n.hUntil)),
    }, n, role);
  }

  // --- AT RISK: unfilled and within 7 days ---
  if (missing > 0 && startingSoon168) {
    return build('at_risk', {
      headline: role === 'client'
        ? `${missing} ${pluralize(missing, 'position')} still open — event starts ${friendlyDate(n.shiftDate)}.`
        : `Needs ${missing} more ${n.roleName} before ${friendlyDate(n.shiftDate)}.`,
      explanation: n.applicants > 0
        ? `${n.applicants} ${pluralize(n.applicants, 'applicant')} waiting for review.`
        : 'No pending applicants yet.',
      urgency: 80 - Math.min(60, Math.max(0, n.hUntil / 3)),
    }, n, role);
  }

  // --- NEEDS APPLICANTS: unfilled with zero applicants ---
  if (missing > 0 && n.applicants === 0) {
    return build('needs_applicants', {
      headline: role === 'client'
        ? `${missing} ${pluralize(missing, 'position')} open — no applicants yet.`
        : `${missing} ${pluralize(missing, 'position')} open — no applicants for ${n.roleName}.`,
      explanation: role === 'admin' || role === 'agency'
        ? 'Invite qualified talent or expand the search radius.'
        : 'We recommend requesting staffing help.',
      urgency: 60,
    }, n, role);
  }

  // --- REVIEWING APPLICANTS: unfilled but applicants pending ---
  if (missing > 0 && n.applicants > 0) {
    return build('reviewing_applicants', {
      headline: `${n.applicants} ${pluralize(n.applicants, 'applicant')} waiting for review.`,
      explanation: role === 'client'
        ? `Approve applicants to fill ${missing} open ${pluralize(missing, 'position')}.`
        : `${missing} open ${pluralize(missing, 'position')} for ${n.roleName} — review & book.`,
      urgency: 50,
    }, n, role);
  }

  // --- BACKUP NEEDED: fully primary-staffed but backups still open ---
  if (n.primaryCount >= n.needed && n.backupsNeeded > 0 && n.backupsHave < n.backupsNeeded) {
    const bMissing = n.backupsNeeded - n.backupsHave;
    return build('backup_needed', {
      headline: `Needs ${bMissing} ${pluralize(bMissing, 'backup')}.`,
      explanation: role === 'client'
        ? 'Backups protect against last-minute cancellations.'
        : `${n.backupsHave}/${n.backupsNeeded} backups confirmed.`,
      urgency: 30,
    }, n, role);
  }

  // --- BRIEFING INCOMPLETE: filled but briefing < 60% and event within 7 days ---
  if (n.staffed >= n.needed && startingSoon168 && n.briefingPct < 60 && !opts.skipBriefing) {
    return build('briefing_incomplete', {
      headline: role === 'client'
        ? `Event briefing is ${n.briefingPct}% complete.`
        : `Briefing only ${n.briefingPct}% complete — event ${friendlyDate(n.shiftDate)}.`,
      explanation: role === 'client'
        ? 'Add briefing details so talent arrives prepared.'
        : 'Missing details block confirmation nudges.',
      urgency: 25,
    }, n, role);
  }

  // --- AWAITING CONFIRMATION: within 72h, day-of unconfirmed / missed ---
  if (n.staffed >= n.needed && n.hUntil !== null && n.hUntil <= 72 && n.dayMissed > 0) {
    return build('awaiting_confirmation', {
      headline: `${n.dayMissed} ${pluralize(n.dayMissed, 'person', 'people')} missed day-of check-in.`,
      explanation: role === 'client'
        ? 'A backup may need to be promoted.'
        : 'Consider promoting a backup or sending a reminder.',
      urgency: 55,
    }, n, role);
  }

  // --- PARTIALLY STAFFED (fallback for open positions >7 days out) ---
  if (missing > 0) {
    return build('partially_staffed', {
      headline: `${n.staffed}/${n.needed} filled — ${missing} still open.`,
      explanation: n.applicants > 0
        ? `${n.applicants} ${pluralize(n.applicants, 'applicant')} awaiting review.`
        : `Event ${friendlyDate(n.shiftDate)}.`,
      urgency: 20,
    }, n, role);
  }

  // --- FULLY STAFFED ---
  return build('fully_staffed', {
    headline: role === 'client'
      ? `Fully staffed — all ${n.needed} ${pluralize(n.needed, 'position')} confirmed.`
      : `Fully staffed · ${n.staffed}/${n.needed}.`,
    explanation: n.backupsNeeded > 0 && n.backupsHave >= n.backupsNeeded
      ? `Plus ${n.backupsHave} ${pluralize(n.backupsHave, 'backup')} confirmed.`
      : '',
    urgency: 10,
  }, n, role);
}

/* ------------------------------------------------------------ builders --- */

function build(categoryId, payload, n, role) {
  const cat = CATEGORY_LOOKUP[categoryId] || CATEGORY_LOOKUP.partially_staffed;
  return {
    category:        cat.id,
    categoryLabel:   cat.label,
    tone:            cat.tone,
    order:           cat.order,
    headline:        payload.headline,
    explanation:     payload.explanation || '',
    urgencyScore:    Math.max(0, Math.min(100, Math.round(payload.urgency))),
    hoursUntilStart: n.hUntil,
    missingCount:    Math.max(0, n.needed - n.staffed),
    actions:         recommendActions(cat.id, n, role),
  };
}

/**
 * Returns the recommended actions for this category / role. Every
 * action carries a stable `id` — the UI decides what to do on click
 * (Phase 2 will make one-tap execution real).
 */
function recommendActions(categoryId, n, role) {
  const actions = [];
  const push = (id, label) => actions.push({ id, label, testid: `ai-action-${id}` });

  switch (categoryId) {
    case 'critical':
    case 'at_risk':
      if (n.applicants > 0) push('review_applicants', 'Review Applicants');
      push('invite_more', 'Invite More Talent');
      if (n.backupsHave > 0) push('promote_backup', 'Promote Backup');
      if (role === 'client') push('request_help', 'Request Staffing Help');
      break;
    case 'needs_applicants':
      push('invite_more', 'Invite More Talent');
      if (role === 'client') push('request_help', 'Request Staffing Help');
      else push('expand_radius', 'Expand Search Radius');
      break;
    case 'reviewing_applicants':
      push('review_applicants', 'Review Applicants');
      push('invite_more', 'Invite More Talent');
      break;
    case 'awaiting_confirmation':
      push('send_reminder', 'Send Confirmation Reminder');
      if (n.backupsHave > 0) push('promote_backup', 'Promote Backup');
      break;
    case 'backup_needed':
      push('invite_backup', 'Invite Backup Talent');
      break;
    case 'fully_staffed':
      push('view_shift', 'View Shift');
      if (n.backupsNeeded > n.backupsHave) push('invite_backup', 'Add Backup');
      break;
    case 'briefing_incomplete':
      push('request_client_briefing', 'Ask Client to Complete');
      push('complete_briefing', 'Open Briefing');
      break;
    case 'partially_staffed':
      if (n.applicants > 0) push('review_applicants', 'Review Applicants');
      push('invite_more', 'Invite More Talent');
      break;
    case 'in_progress':
      push('view_shift', 'View Live Board');
      break;
    case 'completed':
      push('view_shift', 'View Recap');
      break;
    default:
      push('view_shift', 'View Shift');
  }
  return actions;
}

/* ---------------------------------------------------- summary aggregate --- */

/**
 * Bucket a list of shifts by their AI category. Handy for building the
 * clickable summary tiles at the top of Jobs and Shifts pages.
 *
 * @param {Array} shifts
 * @param {object} opts – forwarded to classifyShift
 * @returns { intelligenceById, countsByCategory, totals }
 */
export function summarize(shifts, opts = {}) {
  const intelligenceById = {};
  const countsByCategory = Object.fromEntries(CATEGORIES.map((c) => [c.id, 0]));
  let totalOpenPositions = 0;
  let totalApplicantsPending = 0;
  let totalStartingSoon = 0;
  let totalFullyStaffed = 0;
  let totalAttention = 0;   // sum across critical/at_risk/needs/reviewing/awaiting/backup

  for (const s of shifts) {
    const intel = classifyShift(s, opts);
    intelligenceById[s.id] = intel;
    countsByCategory[intel.category] = (countsByCategory[intel.category] || 0) + 1;
    const n = normalise(s);
    if (n.needed > n.staffed) totalOpenPositions += (n.needed - n.staffed);
    if (n.applicants > 0)     totalApplicantsPending += n.applicants;
    if (intel.hoursUntilStart !== null && intel.hoursUntilStart > 0 && intel.hoursUntilStart <= 48) {
      totalStartingSoon += 1;
    }
    if (intel.category === 'fully_staffed') totalFullyStaffed += 1;
    if (['critical', 'at_risk', 'needs_applicants', 'reviewing_applicants', 'awaiting_confirmation', 'backup_needed'].includes(intel.category)) {
      totalAttention += 1;
    }
  }

  return {
    intelligenceById,
    countsByCategory,
    totals: {
      shifts:              shifts.length,
      openPositions:       totalOpenPositions,
      applicantsPending:   totalApplicantsPending,
      startingSoon:        totalStartingSoon,
      fullyStaffed:        totalFullyStaffed,
      attention:           totalAttention,
    },
  };
}
